import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  message,
  Popconfirm,
  Tag,
  Typography,
  Tooltip,
  Modal,
  Form,
  Select,
  InputNumber,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ToolOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import { ServiceTemplate } from '../../types';
import { serviceTemplateApi } from '../../services/api';

const { Title, Text } = Typography;
const { Search } = Input;
const { TextArea } = Input;

const ServiceTemplateList: React.FC = () => {
  const [templates, setTemplates] = useState<ServiceTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [categories, setCategories] = useState<string[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ServiceTemplate | null>(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    fetchTemplates();
    fetchCategories();
  }, [searchTerm, categoryFilter]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await serviceTemplateApi.getAll({
        search: searchTerm || undefined,
        category: categoryFilter === 'all' ? undefined : categoryFilter,
      });
      setTemplates(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching service templates:', error);
      message.error('Failed to fetch service templates');
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await serviceTemplateApi.getCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleEdit = (template: ServiceTemplate) => {
    setEditingTemplate(template);
    form.setFieldsValue({
      ...template,
      common_parts: template.common_parts?.join('\n') || '',
    });
    setModalVisible(true);
  };

  const handleDelete = async (templateId: string) => {
    try {
      await serviceTemplateApi.delete(templateId);
      message.success('Service template deleted successfully');
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting service template:', error);
      message.error('Failed to delete service template');
    }
  };

  const handleSaveTemplate = async () => {
    try {
      const values = await form.validateFields();
      const templateData = {
        ...values,
        common_parts: values.common_parts ? values.common_parts.split('\n').map((p: string) => p.trim()).filter((p: string) => p) : [],
      };

      if (editingTemplate) {
        await serviceTemplateApi.update(editingTemplate.id, templateData);
        message.success('Service template updated successfully');
      } else {
        await serviceTemplateApi.create({ id: values.id, ...templateData });
        message.success('Service template created successfully');
      }

      setModalVisible(false);
      setEditingTemplate(null);
      form.resetFields();
      fetchTemplates();
    } catch (error) {
      console.error('Error saving service template:', error);
      message.error('Failed to save service template');
    }
  };

  const handleNewTemplate = () => {
    setEditingTemplate(null);
    form.resetFields();
    setModalVisible(true);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      render: (id: string) => (
        <Text code style={{ fontSize: '12px' }}>{id}</Text>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: ServiceTemplate) => (
        <div>
          <Text strong>{name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.description}
          </Text>
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (category: string) => (
        <Tag color="blue">{category}</Tag>
      ),
    },
    {
      title: 'Hours',
      dataIndex: 'estimated_hours',
      key: 'estimated_hours',
      width: 80,
      align: 'center' as const,
      render: (hours: number) => `${hours}h`,
    },
    {
      title: 'Base Price',
      dataIndex: 'base_price',
      key: 'base_price',
      width: 100,
      align: 'right' as const,
      render: (price: number) => `$${price.toFixed(2)}`,
    },
    {
      title: 'Common Parts',
      dataIndex: 'common_parts',
      key: 'common_parts',
      width: 150,
      render: (parts: string[]) => (
        <div>
          {parts && parts.length > 0 ? (
            <Tooltip title={parts.join(', ')}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {parts.length} part{parts.length !== 1 ? 's' : ''}
              </Text>
            </Tooltip>
          ) : (
            <Text type="secondary" style={{ fontSize: '12px' }}>No parts</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      align: 'center' as const,
      render: (_: any, record: ServiceTemplate) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete Template"
            description="Are you sure you want to delete this service template?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                danger
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <ToolOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
              <Title level={3} style={{ margin: 0 }}>Service Templates</Title>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleNewTemplate}
            >
              New Template
            </Button>
          </div>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Search
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onSearch={fetchTemplates}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                style={{ width: '100%' }}
                placeholder="Filter by category"
                value={categoryFilter}
                onChange={setCategoryFilter}
                options={[
                  { label: 'All Categories', value: 'all' },
                  ...categories.map(cat => ({ label: cat, value: cat })),
                ]}
              />
            </Col>
          </Row>
        </div>

        <Table
          columns={columns}
          dataSource={templates}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} templates`,
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Modal
        title={editingTemplate ? 'Edit Service Template' : 'New Service Template'}
        open={modalVisible}
        onOk={handleSaveTemplate}
        onCancel={() => {
          setModalVisible(false);
          setEditingTemplate(null);
          form.resetFields();
        }}
        width={600}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
        >
          <Form.Item
            name="id"
            label="Template ID"
            rules={[{ required: true, message: 'Please enter template ID' }]}
          >
            <Input
              placeholder="e.g., oil-change-basic"
              disabled={!!editingTemplate}
            />
          </Form.Item>

          <Form.Item
            name="name"
            label="Service Name"
            rules={[{ required: true, message: 'Please enter service name' }]}
          >
            <Input placeholder="e.g., Basic Oil Change" />
          </Form.Item>

          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: 'Please enter category' }]}
          >
            <Input placeholder="e.g., Maintenance" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <TextArea
              rows={3}
              placeholder="Detailed description of the service..."
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="estimated_hours"
                label="Estimated Hours"
                rules={[{ required: true, message: 'Please enter estimated hours' }]}
              >
                <InputNumber
                  min={0}
                  step={0.25}
                  placeholder="2.5"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="base_price"
                label="Base Price ($)"
                rules={[{ required: true, message: 'Please enter base price' }]}
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  placeholder="89.99"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="common_parts"
            label="Common Parts (one per line)"
          >
            <TextArea
              rows={4}
              placeholder={`Oil Filter\nOil Drain Plug\nEngine Oil (5W-30)`}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ServiceTemplateList;